'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';

// Mobile bottom nav — SHOW (home feed) / ME (profile, center orb) / TRADE (post a trade).
// design_handoff_bottom_nav, variant 1c, 2026-09-08 — replaces the old 5-icon bar
// (Home/Ranks/Post/Alerts/Menu); those destinations stay reachable via the top Navbar
// (desktop nav + mobile hamburger, which already mirrors Forum/Live/Traders/etc.) and
// the notification bell that lives there too.
const HIDDEN_PREFIXES = ['/sign-up-login', '/auth', '/invite', '/forgot-password', '/password-reset', '/token-activation', '/channels/create'];
const MUTED = '#8B92A0';
const LIME = '#C8F751';
const CAPSULE_STYLE: React.CSSProperties = {
  width: 118, height: 58, borderRadius: 29,
  background: '#14161A', border: '1px solid rgba(255,255,255,0.06)',
  boxShadow: '0 10px 26px rgba(0,0,0,0.5)',
};
const LABEL_FONT = { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: '0.08em' } as const;

export default function MobileBottomNav() {
  const pathname = usePathname() || '';
  const router = useRouter();
  const { user } = useAuth();
  const { t } = useLang();

  const hidden = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
  if (hidden || !user) return null;

  const isShow = pathname === '/' || pathname.startsWith('/home-feed');
  const isMe = pathname.startsWith('/user-profile') || pathname.startsWith('/profile');

  function handleTrade() {
    if (isShow) window.dispatchEvent(new Event('smt:open-post-modal'));
    else router.push('/home-feed?post=true');
  }

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 md:hidden" aria-label={t('Primary navigation')}>
      <Link
        href="/"
        aria-current={isShow ? 'page' : undefined}
        className="absolute flex items-center justify-center gap-2 active:scale-95 transition-transform duration-150"
        style={{ bottom: 'calc(30px + env(safe-area-inset-bottom))', left: 16, ...CAPSULE_STYLE }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 12c2.5-5 6-7.5 9-7.5s6.5 2.5 9 7.5c-2.5 5-6 7.5-9 7.5s-6.5-2.5-9-7.5z" stroke={isShow ? LIME : MUTED} strokeWidth="1.8" />
          <circle cx="12" cy="12" r="2.6" fill={isShow ? LIME : MUTED} />
        </svg>
        <span style={{ ...LABEL_FONT, color: isShow ? LIME : MUTED }}>SHOW</span>
      </Link>

      <button
        type="button"
        onClick={handleTrade}
        aria-label={t('Post a trade')}
        className="absolute flex items-center justify-center gap-2 active:scale-95 transition-transform duration-150"
        style={{ bottom: 'calc(30px + env(safe-area-inset-bottom))', right: 16, cursor: 'pointer', ...CAPSULE_STYLE }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 16l6-4 4 2.5 6-8" stroke={MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ ...LABEL_FONT, color: MUTED }}>TRADE</span>
      </button>

      <Link
        href="/user-profile"
        aria-current={isMe ? 'page' : undefined}
        className="absolute"
        style={{ bottom: 'calc(12px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)' }}
      >
        <div
          className="flex flex-col items-center justify-center active:scale-95 transition-transform duration-150"
          style={{
            width: 90, height: 90, borderRadius: '50%', gap: 3,
            background: 'radial-gradient(circle at 32% 28%, #DEFF8C, #C8F751 60%)',
            boxShadow: isMe
              ? '0 16px 34px rgba(200,247,81,0.55), 0 0 0 7px #0A0B0D'
              : '0 14px 30px rgba(200,247,81,0.45), 0 0 0 7px #0A0B0D',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 15l6-6 4 4 8-9" stroke="#0A0B0D" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="21" cy="4" r="2" fill="#0A0B0D" />
          </svg>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', color: '#0A0B0D' }}>ME</span>
        </div>
      </Link>
    </nav>
  );
}
